import { supabase } from "../lib/supabase";

const unwrap = ({ data, error }) => {
  if (error) throw error;
  return data;
};

const emptyDashboard = {
  metrics: {},
  spaces: [],
  bookings: [],
  work_orders: [],
  assets: [],
  compliance: [],
  utilities: [],
  vendors: [],
};

const call = async (functionName, params) =>
  unwrap(await supabase.rpc(functionName, params));

const BOOKING_DOCUMENT_BUCKET = "estates-booking-documents";
const MAX_BOOKING_DOCUMENT_BYTES = 5 * 1024 * 1024;
const BOOKING_DOCUMENT_TYPES = ["application/pdf", "image/jpeg", "image/png"];

const uploadBookingDocument = async (document) => {
  if (!document) {
    throw new Error("Upload the approval document for this meeting request.");
  }
  if (
    document.size <= 0 ||
    document.size > MAX_BOOKING_DOCUMENT_BYTES ||
    !BOOKING_DOCUMENT_TYPES.includes(document.type)
  ) {
    throw new Error("Approval documents must be a PDF, JPG or PNG up to 5 MB.");
  }
  const {
    data: { user },
    error: userError,
  } = await supabase.auth.getUser();
  if (userError || !user?.id) {
    throw userError || new Error("Authentication is required to upload the approval document.");
  }
  const safeName = document.name.replace(/[^a-zA-Z0-9._-]/g, "_").slice(-120);
  const randomId = globalThis.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  const path = `${user.id}/${randomId}-${safeName}`;
  const { data, error } = await supabase.storage
    .from(BOOKING_DOCUMENT_BUCKET)
    .upload(path, document, { contentType: document.type, upsert: false });
  if (error) throw error;
  return {
    path: data?.path || path,
    name: document.name,
    size: document.size,
    mimeType: document.type,
  };
};

const estatesService = {
  async releaseExpiredBookings() {
    return call("estates_release_expired_bookings");
  },

  async getDashboard() {
    return (await call("estates_staff_dashboard")) || emptyDashboard;
  },

  async createWorkOrder({
    title,
    description,
    spaceId = null,
    assetId = null,
    trade = "general",
    priority = "normal",
  }) {
    return call("estates_create_work_order", {
      p_title: title,
      p_description: description,
      p_space_id: spaceId || null,
      p_asset_id: assetId || null,
      p_trade: trade,
      p_priority: priority,
    });
  },

  async createBooking({
    spaceId,
    title,
    purpose = "",
    startsAt,
    endsAt,
    attendees = 1,
    externalRequest = false,
    services = [],
    notes = "",
    meetingNotice = "",
    serviceRequests = [],
    approvalDocument = null,
  }) {
    const uploadedDocument = await uploadBookingDocument(approvalDocument);
    try {
      return await call("estates_create_booking", {
        p_space_id: spaceId,
        p_title: title,
        p_purpose: purpose,
        p_starts_at: startsAt,
        p_ends_at: endsAt,
        p_attendees: Number(attendees || 1),
        p_external_request: Boolean(externalRequest),
        p_services: services,
        p_notes: notes,
        p_approval_document_path: uploadedDocument.path,
        p_approval_document_name: uploadedDocument.name,
        p_approval_document_size: uploadedDocument.size,
        p_approval_document_mime_type: uploadedDocument.mimeType,
        p_meeting_notice: meetingNotice,
        p_service_requests: serviceRequests,
      });
    } catch (error) {
      await supabase.storage.from(BOOKING_DOCUMENT_BUCKET).remove([uploadedDocument.path]);
      throw error;
    }
  },

  async createSpace({
    code,
    name,
    building,
    floor = "",
    spaceType = "classroom",
    capacity = 1,
    status = "ACTIVE",
  }) {
    return call("estates_create_space", {
      p_code: code || null,
      p_name: name,
      p_building: building,
      p_floor: floor,
      p_space_type: spaceType,
      p_capacity: Number(capacity || 1),
      p_status: status,
    });
  },

  async updateSpace({
    id,
    code,
    name,
    building,
    floor = "",
    spaceType = "classroom",
    capacity = 1,
    status = "ACTIVE",
  }) {
    return call("estates_update_space", {
      p_space_id: id,
      p_code: code,
      p_name: name,
      p_building: building,
      p_floor: floor,
      p_space_type: spaceType,
      p_capacity: Number(capacity || 1),
      p_status: status,
    });
  },

  async updateBookingStatus({ id, status, notes = "" }) {
    return call("estates_update_booking_status", {
      p_booking_id: id,
      p_status: status,
      p_notes: notes,
    });
  },

  async getBookingCoordinators(bookingId) {
    return (await call("estates_booking_coordinators", {
      p_booking_id: bookingId,
    })) || [];
  },

  async allocateBooking({ id, coordinatorId, notes = "" }) {
    return call("estates_allocate_booking", {
      p_booking_id: id,
      p_coordinator_id: coordinatorId,
      p_notes: notes,
    });
  },

  async getServiceRequests(serviceDepartment = null) {
    return (await call("estates_get_service_requests", {
      p_service_department: serviceDepartment,
    })) || [];
  },

  async getServiceRepresentatives(serviceDepartment) {
    return (await call("estates_service_representatives", {
      p_service_department: serviceDepartment,
    })) || [];
  },

  async assignServiceRequest({ id, representativeId, status = "ASSIGNED", notes = "" }) {
    return call("estates_assign_service_request", {
      p_request_id: id,
      p_representative_id: representativeId,
      p_status: status,
      p_notes: notes,
    });
  },

  async getBookingDocumentUrl(filePath, expiresIn = 900) {
    if (!filePath) throw new Error("This booking has no approval document.");
    const { data, error } = await supabase.storage
      .from(BOOKING_DOCUMENT_BUCKET)
      .createSignedUrl(filePath, expiresIn);
    if (error) throw error;
    if (!data?.signedUrl) throw new Error("The approval document could not be opened.");
    return data.signedUrl;
  },

  async getAvailableSpaces() {
    const { data, error } = await supabase
      .from("estates_spaces")
      .select("id, code, name, building, floor, space_type, capacity")
      .eq("status", "ACTIVE")
      .is("owner_department_id", null)
      .order("building")
      .order("code");
    if (error) throw error;
    return data || [];
  },

  async getServiceCatalogue() {
    const { data, error } = await supabase
      .from("estates_booking_service_catalogue")
      .select("service_code, service_name, service_department, description")
      .eq("active", true)
      .order("service_name");
    if (error) throw error;
    return data || [];
  },

  async getMyBookings() {
    await this.releaseExpiredBookings();
    const { data, error } = await supabase
      .from("estates_bookings")
      .select(
        "id, booking_number, title, purpose, starts_at, ends_at, status, attendees, external_request, created_at, meeting_notice, approval_document_path, approval_document_name, services, space:estates_spaces(code, name, building)",
      )
      .order("created_at", { ascending: false });
    if (error) throw error;
    return data || [];
  },
};

export { estatesService, emptyDashboard };
export default estatesService;
